import { editorToSap } from './cmdConfig';

/** =========================
 *  CONFIG DE FORMATAÇÃO
 *  ======================== */
export const defaultFormatOptions = {
  indent: 2,
  trailingComma: false,
  spaceAfterColon: true,
  keyOrder: [
    'id', 'modulo', 'idx', 'descricao',
    'instances',
    'tipo',
    'x', 'y', 'width', 'height',
    'circleNumber',
    'ledPath', 'ledColor', 'ledMode', 'ledStrokeWidth'
  ],
  inlineObjectsInArrays: true,
  inlineObjectMaxLength: 200,
  quoteKeysAlways: true // sobrescrevemos no JS final
};

/** =========================
 *  HELPERS
 *  ======================== */

const isPrimitive = (v) =>
  v === null || ['string', 'number', 'boolean'].includes(typeof v);

const isFlatObject = (obj) => {
  if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) return false;
  return Object.values(obj).every(isPrimitive);
};

const isValidIdentifier = (key) => /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(key);

const escapeString = (str = '') =>
  String(str).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

const orderObjectKeys = (obj, keyOrder) => {
  const keys = Object.keys(obj);
  const prioritized = keyOrder.filter((k) => keys.includes(k));
  const rest = keys.filter((k) => !keyOrder.includes(k));
  return [...prioritized, ...rest];
};

/** Pretty printer para gerar um literal JS/JSON bonitinho */
export const serializeToJsPretty = (
  value,
  options = defaultFormatOptions,
  level = 0,
  { parentIsArray = false } = {}
) => {
  const {
    indent,
    trailingComma,
    spaceAfterColon,
    keyOrder,
    inlineObjectsInArrays,
    inlineObjectMaxLength,
    quoteKeysAlways
  } = options;

  const pad = (n) => ' '.repeat(n * indent);
  const colon = spaceAfterColon ? ': ' : ':';

  if (value === null) return 'null';

  const t = typeof value;
  if (t === 'string') return `"${escapeString(value)}"`;
  if (t === 'number' || t === 'boolean') return String(value);

  // ===== Arrays =====
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    const items = value.map((v, i) => {
      const last = i === value.length - 1;
      const comma = last ? '' : ',';
      const rendered = serializeToJsPretty(v, options, level + 1, {
        parentIsArray: true
      });
      return `${pad(level + 1)}${rendered}${comma}`;
    });
    const endComma = trailingComma ? ',' : '';
    return `[\n${items.join('\n')}\n${pad(level)}]${endComma}`;
  }

  // ===== Objetos =====
  if (t === 'object') {
    // Objetos "planos" dentro de array => inline em uma linha
    if (parentIsArray && inlineObjectsInArrays && isFlatObject(value)) {
      const keys = orderObjectKeys(value, keyOrder).filter(
        (k) => typeof value[k] !== 'undefined'
      );
      const parts = keys.map((k) => {
        const key = quoteKeysAlways
          ? `"${escapeString(k)}"`
          : isValidIdentifier(k)
          ? k
          : `"${escapeString(k)}"`;
        const val = value[k];
        const rendered = serializeToJsPretty(val, options, level + 1, {
          parentIsArray: false
        });
        return `${key}${colon}${rendered}`;
      });
      const inline = `{ ${parts.join(', ')} }`;

      // 🔥 Regra especial: instâncias de light/led SEMPRE inline,
      // mesmo que a linha fique comprida por causa do ledPath
      if (value.tipo === 'light' || value.tipo === 'led') {
        return inline;
      }

      if (inline.length <= inlineObjectMaxLength) {
        return inline;
      }
      // se passar do limite, cai pro modo multiline abaixo
    }

    const keys = orderObjectKeys(value, keyOrder).filter(
      (k) => typeof value[k] !== 'undefined'
    );
    if (keys.length === 0) return '{}';

    const lines = keys.map((k, i) => {
      const key = quoteKeysAlways
        ? `"${escapeString(k)}"`
        : isValidIdentifier(k)
        ? k
        : `"${escapeString(k)}"`;

      const last = i === keys.length - 1;
      const comma = last ? '' : ',';
      const rendered = serializeToJsPretty(value[k], options, level + 1, {
        parentIsArray: false
      });
      return `${pad(level + 1)}${key}${colon}${rendered}${comma}`;
    });

    const endComma = trailingComma ? ',' : '';
    return `{\n${lines.join('\n')}\n${pad(level)}}${endComma}`;
  }

  return 'undefined';
};

// Tenta descobrir a "ordem" do grupo:
// 1) pelo número dentro do id (grupo01, grupo2, etc.)
// 2) se não tiver, tenta pelo circleNumber
// 3) fallback: 9999
function getGroupOrderKey(group) {
  if (typeof group.id === 'string') {
    const match = group.id.match(/(\d+)/);
    if (match) return parseInt(match[1], 10);
  }
  if (group.circleNumber != null) {
    const n = Number(group.circleNumber);
    if (!Number.isNaN(n)) return n;
  }
  return 9999;
}

/**
 * Monta o JS final:
 *  - converte lightPoints (centro) -> formato novo (top-left)
 *  - ordena grupos
 *  - gera:
 *    - jsString  = export const iluminacaoConfig = [...]
 *    - jsonBody  = JSON puro pra validação
 */
export function buildJsExport(
  lightPoints = [],
  exportName = 'iluminacaoConfig',
  formatOptions = defaultFormatOptions
) {
  if (!lightPoints.length) {
    return { jsString: '', jsonBody: '' };
  }

  // 1) Converte centro -> top-left (formato final)
  const raw = editorToSap(lightPoints);

  // 2) Ordena grupos
  const sapConfig = [...raw].sort((a, b) => {
    const ka = getGroupOrderKey(a);
    const kb = getGroupOrderKey(b);
    if (ka !== kb) return ka - kb;
    const idA = a.id || '';
    const idB = b.id || '';
    return idA.localeCompare(idB);
  });

  // 3) JSON puro pra validação no JsOutput
  const jsonBody = JSON.stringify(sapConfig, null, 2);

  // 4) JS bonitão com chaves sem aspas
  const jsBody = serializeToJsPretty(sapConfig, {
    ...formatOptions,
    quoteKeysAlways: false,
    trailingComma: false
  });

  const jsString = `export const ${exportName} = ${jsBody};`;

  return { jsString, jsonBody };
}